import React, { useState, useEffect } from 'react';
import { useBranding } from '../hooks/useBranding';
import { vendorRegistrationService } from '../services/vendorRegistration.service';
import { CheckCircle, XCircle, Clock, AlertCircle, Search } from 'lucide-react';
import { formatDate as formatAppDate } from '../utils/dateUtils';

const VendorRegistrations = () => {
    const { colors } = useBranding();
    const [registrations, setRegistrations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedRegistration, setSelectedRegistration] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [rejectionReason, setRejectionReason] = useState('');
    const [processingAction, setProcessingAction] = useState(false);
    const [actionError, setActionError] = useState('');
    const [filters, setFilters] = useState({
        approval_status: '',
        entity_type: '',
        registration_type: ''
    });
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        loadRegistrations();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]);

    const loadRegistrations = async () => {
        try {
            setLoading(true);
            setError('');
            const data = await vendorRegistrationService.getVendorRegistrations(filters);
            setRegistrations(data);
        } catch (err) {
            console.error('Error loading registrations:', err);
            setError('Failed to load vendor registrations');
        } finally {
            setLoading(false);
        }
    };

    const handleViewDetails = async (registration) => {
        try {
            const detailedRegistration = await vendorRegistrationService.getVendorRegistrationById(registration.id);
            console.log('Registration details loaded:', {
                id: detailedRegistration.id,
                type: detailedRegistration.registration_type,
                entity: detailedRegistration.entity_type,
                dataKeys: Object.keys(detailedRegistration.registration_data || {}),
                data: detailedRegistration.registration_data
            });
            setSelectedRegistration(detailedRegistration);
            setActionError('');
            setShowModal(true);
        } catch (err) {
            console.error('Error loading registration details:', err);
            setError('Failed to load registration details');
        }
    };

    const handleApprove = async () => {
        if (!selectedRegistration) return;
        
        try {
            setProcessingAction(true);
            setActionError('');
            await vendorRegistrationService.approveVendorRegistration(selectedRegistration.id);
            setShowModal(false);
            setSelectedRegistration(null);
            loadRegistrations(); // Refresh the list
        } catch (err) {
            console.error('Error approving registration:', err);
            setActionError(err?.message || 'Failed to approve registration. Please try again.');
        } finally {
            setProcessingAction(false);
        }
    };

    const handleReject = async () => {
        if (!selectedRegistration || !rejectionReason.trim()) return;
        
        try {
            setProcessingAction(true);
            setActionError('');
            await vendorRegistrationService.rejectVendorRegistration(selectedRegistration.id, rejectionReason);
            setShowModal(false);
            setShowRejectModal(false);
            setSelectedRegistration(null);
            setRejectionReason('');
            loadRegistrations(); // Refresh the list
        } catch (err) {
            console.error('Error rejecting registration:', err);
            setActionError(err?.message || 'Failed to reject registration. Please try again.');
        } finally {
            setProcessingAction(false);
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'pending':
                return <Clock className="w-4 h-4 text-yellow-500" />;
            case 'approved':
                return <CheckCircle className="w-4 h-4 text-green-500" />;
            case 'rejected':
                return <XCircle className="w-4 h-4 text-red-500" />;
            case 'draft':
                return <AlertCircle className="w-4 h-4 text-gray-500" />;
            default:
                return <AlertCircle className="w-4 h-4 text-gray-500" />;
        }
    };

    const getStatusBadge = (status) => {
        const baseClasses = "px-2 py-1 text-xs font-medium rounded-full";
        switch (status) {
            case 'pending':
                return `${baseClasses} bg-yellow-100 text-yellow-800`;
            case 'approved':
                return `${baseClasses} bg-green-100 text-green-800`;
            case 'rejected':
                return `${baseClasses} bg-red-100 text-red-800`;
            case 'draft':
                return `${baseClasses} bg-gray-100 text-gray-800`;
            default:
                return `${baseClasses} bg-gray-100 text-gray-800`;
        }
    };

    const formatDate = (dateString) => formatAppDate(dateString);

    const filteredRegistrations = registrations.filter(registration => {
        const searchLower = searchTerm.toLowerCase();
        return (
            registration.id.toString().includes(searchLower) ||
            registration.user_mobile_number.includes(searchLower) ||
            registration.registration_type.toLowerCase().includes(searchLower) ||
            registration.entity_type.toLowerCase().includes(searchLower)
        );
    });

    const renderRegistrationDetails = () => {
        if (!selectedRegistration) return null;

        const { registration_data } = selectedRegistration;

        // Helper function to render any value (handles objects, arrays, primitives)
        const renderValue = (value, key = '') => {
            if (value === null || value === undefined) {
                return <span className="text-gray-400 italic">null</span>;
            }
            
            if (typeof value === 'object' && !Array.isArray(value)) {
                return (
                    <div className="ml-4 border-l-2 border-gray-200 pl-3">
                        {Object.entries(value).map(([subKey, subValue]) => (
                            <div key={subKey} className="mb-2">
                                <span className="font-medium text-gray-700">{subKey}:</span>
                                {renderValue(subValue, subKey)}
                            </div>
                        ))}
                    </div>
                );
            }
            
            if (Array.isArray(value)) {
                if (value.length === 0) {
                    return <span className="text-gray-400 italic">Empty array</span>;
                }
                return (
                    <div className="ml-4 border-l-2 border-blue-200 pl-3">
                        {value.map((item, index) => (
                            <div key={index} className="mb-2 p-2 bg-blue-50 rounded">
                                <span className="font-medium text-gray-700">Item {index + 1}:</span>
                                {renderValue(item, `${key}[${index}]`)}
                            </div>
                        ))}
                    </div>
                );
            }
            
            if (typeof value === 'string' && value.trim() === '') {
                return <span className="text-gray-400 italic">Empty string</span>;
            }
            
            if (typeof value === 'boolean') {
                return <span className={`font-medium ${value ? 'text-green-600' : 'text-red-600'}`}>{value.toString()}</span>;
            }
            
            if (typeof value === 'number') {
                return <span className="text-blue-600 font-medium">{value}</span>;
            }
            
            return <span className="text-gray-900">{value}</span>;
        };

        // Render all registration data as-is
        if (!registration_data || Object.keys(registration_data).length === 0) {
            return (
                <div className="space-y-4">
                    <div className="bg-red-50 rounded-lg p-3 border border-red-200">
                        <h4 className="text-sm font-medium text-red-900 mb-2">No Registration Data</h4>
                        <div className="text-xs text-red-800">
                            This registration has no data to display. The registration_data field is empty or null.
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <div className="space-y-4">
                <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                    <h4 className="text-sm font-medium text-blue-900 mb-3">Registration Data Structure</h4>
                    <div className="text-xs space-y-2">
                        {Object.entries(registration_data).map(([key, value]) => (
                            <div key={key} className="border-b border-blue-100 pb-2 last:border-b-0">
                                <div className="font-semibold text-blue-800 text-sm mb-1 capitalize">
                                    {key.replace(/_/g, ' ')}:
                                </div>
                                {renderValue(value, key)}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="p-6">
            <div className="mb-6">
                <h1 className="text-2xl font-semibold text-gray-900 mb-2">Vendor Registrations</h1>
                <p className="text-gray-600">Review and manage vendor registration requests</p>
            </div>

            {error && (
                <div className="mb-4 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
                    {error}
                </div>
            )}

            {/* Filters and Search */}
            <div className="mb-6 bg-white rounded-lg shadow-sm border p-4">
                <div className="flex flex-col md:flex-row gap-4">
                    <div className="flex-1">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                            <input
                                type="text"
                                placeholder="Search by ID, mobile, or registration type..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>
                    </div>
                    
                    <div className="flex gap-2">
                        <select
                            value={filters.approval_status}
                            onChange={(e) => setFilters(prev => ({ ...prev, approval_status: e.target.value }))}
                            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                            <option value="">All Status</option>
                            <option value="draft">Draft</option>
                            <option value="pending">Pending</option>
                            <option value="approved">Approved</option>
                            <option value="rejected">Rejected</option>
                            <option value="withdrawn">Withdrawn</option>
                        </select>
                        
                        <select
                            value={filters.registration_type}
                            onChange={(e) => setFilters(prev => ({ ...prev, registration_type: e.target.value }))}
                            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                            <option value="">All Registration Types</option>
                            <option value="new_registration">New Registration</option>
                            <option value="existing_record_update">Existing Record Update</option>
                            <option value="new_record_existing_user">New Record Existing User</option>
                        </select>
                        
                        <select
                            value={filters.entity_type}
                            onChange={(e) => setFilters(prev => ({ ...prev, entity_type: e.target.value }))}
                            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                            <option value="">All Entity Types</option>
                            <option value="vendor">Vendor</option>
                            <option value="vendor_location">Location</option>
                            <option value="vendor_driver">Driver</option>
                            <option value="vendor_service_rate">Service Rate</option>
                        </select>
                    </div>
                    
                    <div className="flex gap-2">
                        <button
                            onClick={() => setFilters({ approval_status: '', entity_type: '', registration_type: '' })}
                            className="px-3 py-2 text-sm text-gray-600 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                        >
                            Clear Filters
                        </button>
                    </div>
                </div>
            </div>

            {/* Results Count */}
            {!loading && (
                <div className="mb-4 text-sm text-gray-600">
                    Found {filteredRegistrations.length} registration{filteredRegistrations.length !== 1 ? 's' : ''}
                    {Object.values(filters).some(f => f) && (
                        <span className="ml-2 text-gray-500">
                            (filtered)
                        </span>
                    )}
                </div>
            )}

            {/* Registrations Table */}
            <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
                {loading ? (
                    <div className="p-8 text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto mb-4" style={{ borderColor: colors.primary }}></div>
                        <p className="text-gray-600">Loading registrations...</p>
                    </div>
                ) : filteredRegistrations.length === 0 ? (
                    <div className="p-8 text-center">
                        <p className="text-gray-600">No registrations found</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        ID
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        User Mobile
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Registration Type
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Status
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Submitted
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Entity Type
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {filteredRegistrations.map((registration) => (
                                    <tr 
                                        key={registration.id} 
                                        className="hover:bg-gray-50 cursor-pointer"
                                        onClick={() => handleViewDetails(registration)}
                                    >
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                            #{registration.id}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {registration.user_mobile_number}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            <span className="capitalize">{registration.registration_type.replace('_', ' ')}</span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center space-x-2">
                                                {getStatusIcon(registration.approval_status)}
                                                <span className={getStatusBadge(registration.approval_status)}>
                                                    {registration.approval_status}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {formatDate(registration.submitted_at || registration.created_at)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            <span className="capitalize">{registration.entity_type.replace('_', ' ')}</span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Registration Details Modal */}
            {showModal && selectedRegistration && (
                <div 
                    className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) {
                            setShowModal(false);
                        }
                    }}
                >
                    <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white max-h-[80vh] overflow-y-auto">
                        <div className="mt-3">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-lg font-medium text-gray-900">
                                    Registration #{selectedRegistration.id}
                                </h3>
                                <button
                                    onClick={() => setShowModal(false)}
                                    className="text-gray-400 hover:text-gray-600"
                                >
                                    <XCircle className="w-6 h-6" />
                                </button>
                            </div>

                            <div className="mb-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                    <div>
                                        <span className="font-medium">User Mobile:</span> {selectedRegistration.user_mobile_number}
                                    </div>
                                    <div>
                                        <span className="font-medium">Registration Type:</span> {selectedRegistration.registration_type.replace('_', ' ')}
                                    </div>
                                    <div>
                                        <span className="font-medium">Entity Type:</span> {selectedRegistration.entity_type.replace('_', ' ')}
                                    </div>
                                    <div>
                                        <span className="font-medium">Status:</span> 
                                        <span className={getStatusBadge(selectedRegistration.approval_status)}>
                                            {selectedRegistration.approval_status}
                                        </span>
                                    </div>
                                    <div>
                                        <span className="font-medium">Submitted:</span> {formatDate(selectedRegistration.submitted_at || selectedRegistration.created_at)}
                                    </div>
                                    <div>
                                        <span className="font-medium">Created:</span> {formatDate(selectedRegistration.created_at)}
                                    </div>
                                </div>
                            </div>

                            {renderRegistrationDetails()}

                            {actionError && !showRejectModal && (
                                <div
                                    role="alert"
                                    className="mt-6 flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm"
                                >
                                    <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                                    <div>
                                        <p className="font-medium">Could not approve this registration. No changes were saved.</p>
                                        <p className="mt-1 whitespace-pre-line break-words">{actionError}</p>
                                    </div>
                                </div>
                            )}

                            {/* Action Buttons */}
                            {selectedRegistration.approval_status === 'pending' && (
                                <div className="flex justify-end space-x-3 mt-6 pt-4 border-t">
                                    <button
                                        onClick={() => {
                                            setActionError('');
                                            setShowRejectModal(true);
                                        }}
                                        disabled={processingAction}
                                        className="px-4 py-2 text-sm font-medium text-red-700 bg-red-100 border border-red-300 rounded-md hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50"
                                    >
                                        Reject
                                    </button>
                                    <button
                                        onClick={handleApprove}
                                        disabled={processingAction}
                                        className="px-4 py-2 text-sm font-medium text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                                        style={{ backgroundColor: colors.primary }}
                                    >
                                        {processingAction ? 'Approving...' : 'Approve'}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Rejection Reason Modal */}
            {showRejectModal && (
                <div 
                    className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) {
                            setShowRejectModal(false);
                            setRejectionReason('');
                            setActionError('');
                        }
                    }}
                >
                    <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
                        <div className="mt-3">
                            <h3 className="text-lg font-medium text-gray-900 mb-4">Reject Registration</h3>
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Reason for Rejection *
                                </label>
                                <textarea
                                    value={rejectionReason}
                                    onChange={(e) => setRejectionReason(e.target.value)}
                                    rows={4}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                                    placeholder="Please provide a reason for rejection..."
                                />
                            </div>
                            {actionError && (
                                <div
                                    role="alert"
                                    className="mb-4 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm break-words"
                                >
                                    {actionError}
                                </div>
                            )}
                            <div className="flex justify-end space-x-3">
                                <button
                                    onClick={() => {
                                        setShowRejectModal(false);
                                        setRejectionReason('');
                                        setActionError('');
                                    }}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleReject}
                                    disabled={!rejectionReason.trim() || processingAction}
                                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50"
                                >
                                    {processingAction ? 'Rejecting...' : 'Reject'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default VendorRegistrations; 